<script lang="ts">
  import type { IntegrationDetails, ExternalContacts } from "../../helpers/types";
  import StepSuccessIcon from "./StepSuccessIcon.svelte";
  import { track } from "../../helpers/mixpanel";
  import { selectedBuyer } from "../../stores/buyer";
  import { updateSquareCategories, updateSquareCategory } from "../../api/integrations";
  import {
    type SquareCategoriesQueryParams,
    createSquareCategoriesQuery,
  } from "../../queries/square";
  import { derived } from "svelte/store";
  import TextNew from "../TextNew.svelte";

  export let integration: IntegrationDetails;
  export let contacts: ExternalContacts | null;
  export let stepNumber: number = 0;
  // export let currentStep: number = 0;
  export let isActive: boolean = false;
  export let authenticated: boolean = false;
  export let subtitle: string = "select organisation";

  const sites =
    integration.platform == "Tevalis"
      ? Object.values(contacts?.sites || {})
      : Object.keys(contacts?.sites || {});

  if (!integration.site_id && Object.keys(contacts?.sites || []).length === 1) {
    integration.site_id = Object.keys(contacts?.sites || [])[0];
  }

  $: siteSelected =
    (integration.site_id && sites.includes(integration.site_id)) ||
    (authenticated && !!integration.site_id && !Object.keys(sites).length);

  $: active =
    (stepNumber && authenticated && isActive) || (authenticated && !stepNumber && siteSelected);

  function createSquareCategoriesQueryParams() {
    return derived([selectedBuyer], ([$selectedBuyer]) => {
      return {
        buyerId: $selectedBuyer?.id || "",
        locationId: integration.site_id || "",
      } satisfies SquareCategoriesQueryParams;
    });
  }

  const squareCategoriesQueryParams = createSquareCategoriesQueryParams();
  const squareCategoriesQuery = createSquareCategoriesQuery(squareCategoriesQueryParams);

  const updateCategoryStatus = (e: any) => {
    const categoryId = e.target.value;
    const active = e.target.checked;

    if (!categoryId) {
      return;
    }

    track("integration_square_category_update", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      category_id: categoryId,
      active,
    });

    updateSquareCategory({
      buyer_id: $selectedBuyer?.id || "",
      location_id: integration.site_id || "",
      category_id: categoryId,
      active,
    }).then(() => {
      $squareCategoriesQuery.refetch();
    });
  };

  const toggleCategories = () => {
    const categories = $squareCategoriesQuery.data?.categories || [];
    const allActive = categories.every((category) => category.active);

    const categoryIds = categories.map((category) => category.id);

    updateSquareCategories({
      buyer_id: $selectedBuyer?.id || "",
      location_id: integration.site_id || "",
      category_ids: categoryIds,
      active: !allActive,
    }).then(() => {
      $squareCategoriesQuery.refetch();
    });
  };
</script>

<div id="square-categories" class="container">
  <div class="supply-line-header">
    <StepSuccessIcon success={!!siteSelected} {isActive} {stepNumber} />
    <h4 class="subtitle">{subtitle}</h4>
    <button class="toggle-btn" on:click={toggleCategories}>Toggle all</button>
  </div>
  {#if active}
    <div class="content">
      <div class="category-row">
        <div class="categories-container">
          <ul>
            {#each $squareCategoriesQuery.data?.categories ?? [] as category}
              <li>
                <TextNew size="m" --rk-text-color="var(--rk-text-dark)" --whitespace="nowrap">
                  {category.name}
                </TextNew>
                <input
                  class="category-checkbox"
                  type="checkbox"
                  value={category.id}
                  checked={category.active}
                  on:change={updateCategoryStatus}
                />
              </li>
            {/each}
          </ul>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .container {
    border-radius: 8px;
    background-color: white;
    margin-bottom: 18px;
  }

  .supply-line-header {
    display: flex;
    padding: 18px;
    border-bottom: 1px solid;
    border-color: var(--rekki-neutral-01);
    align-items: center;
  }
  .content {
    padding: 18px;
  }

  .category-row {
    display: flex;
    margin-bottom: 18px;
    align-items: center;
  }

  .categories-container {
    flex: 1;
  }

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;
  }

  li {
    border: none;
    background-color: var(--rekki-neutral-01);
    border-radius: 8px;
    font-size: 18px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    height: 60px;
    padding-left: 8px;
    padding-right: 8px;
    margin-left: 8px;
    min-width: 180px;
    flex: 1;
    margin-bottom: 16px;

    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  li:last-of-type {
    margin-bottom: 0;
  }

  .subtitle {
    font-size: 18px;
    font-weight: bold;
  }

  .category-checkbox {
    appearance: none;

    border-radius: 50%;
    width: 28px;
    height: 28px;

    background-color: var(--rekki-box-background) !important;
    border: 2px solid #999;
  }

  .category-checkbox:checked {
    background-color: var(--rekki-accent) !important;
    border: 6px solid var(--rekki-light);
  }

  .toggle-btn {
    border-radius: 100px;
    padding: 10px 20px;
    background-color: var(--rekki-accent);
    color: var(--rekki-light);
    border: none;
    justify-self: flex-end;
    font-family: "REKKI-OCD";
    white-space: nowrap;
    text-transform: uppercase;
    margin-left: auto;
    font-size: 16px;
  }
</style>
